import { HQ, sportsimPath } from "../sportConfig";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./MatchTipPulse.css";

async function request(path, options) {
  const response = await fetch(sportsimPath(path), { credentials: "same-origin", ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || "Försök igen om en stund.");
  return body;
}

export default function MatchTipPulse({ simulation, matchId, matchStatus }) {
  const [fixture, setFixture] = useState(null);
  const [summary, setSummary] = useState(null);
  const [account, setAccount] = useState(false);
  const [saved, setSaved] = useState(null);
  const [open, setOpen] = useState(false);
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [resultType, setResultType] = useState("");
  const [email, setEmail] = useState("");
  const [waiting, setWaiting] = useState(() => window.sessionStorage.getItem("hq-pulse-waiting") === "1");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const awayInput = useRef(null);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const editorVisible = open || showLogin;
  useEffect(() => {
    if (!editorVisible) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("input")?.focus();
    const closeOnEscape = (event) => {
      if (event.key === "Escape") { setOpen(false); setShowLogin(false); setMessage(""); }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
      trigger.current?.focus();
    };
  }, [editorVisible]);

  useEffect(() => {
    if (!simulation?.key || !matchId) return undefined;
    let active = true;
    setHome("");
    setAway("");
    setResultType("");
    setFixture(null);
    setSummary(null);
    setSaved(null);
    setOpen(false);
    setShowLogin(false);
    Promise.all([
      request(`/matches?simulation=${encodeURIComponent(simulation.key)}`),
      request("/account"),
    ]).then(async ([rows, user]) => {
      if (!active) return;
      const selected = rows.find((row) => String(row.match_id) === String(matchId));
      if (!selected) return;
      setFixture(selected);
      setAccount(user.authenticated);
      const jobs = [request(`/prediction-summary?simulation=${encodeURIComponent(simulation.key)}&match_id=${encodeURIComponent(matchId)}`).catch(() => null)];
      if (user.authenticated) jobs.push(request(`/predictions?simulation=${encodeURIComponent(simulation.key)}`).catch(() => []));
      const [community, tips] = await Promise.all(jobs);
      if (!active) return;
      setSummary(community?.available && community.total >= 2 ? community : null);
      if (tips) setSaved(tips.find((tip) => tip.match_id === selected.id) || null);
    }).catch(() => {});
    return () => { active = false; };
  }, [simulation?.key, matchId]);

  useEffect(() => {
    if (!waiting || account) return undefined;
    let active = true;
    const check = async () => {
      try {
        const status = await request("/auth/status");
        if (!active) return;
        if (status.authenticated) {
          setAccount(true);
          setWaiting(false);
          window.sessionStorage.removeItem("hq-pulse-waiting");
          const tips = await request(`/predictions?simulation=${encodeURIComponent(simulation.key)}`);
          if (active) setSaved(tips.find((tip) => tip.match_id === fixture?.id) || null);
        } else if (!status.pending) {
          setWaiting(false);
          window.sessionStorage.removeItem("hq-pulse-waiting");
          setMessage("Länken har gått ut. Skicka en ny om du vill tippa.");
        }
      } catch (_) { /* Retry transient network errors. */ }
    };
    check();
    const timer = window.setInterval(check, 4000);
    return () => { active = false; window.clearInterval(timer); };
  }, [waiting, account, fixture?.id, simulation?.key]);

  const startEditing = () => {
    if (saved && home === "" && away === "") {
      setHome(String(saved.pred_home));
      setAway(String(saved.pred_away));
      setResultType(saved.pred_result_type || "");
    }
    setMessage("");
    setShowLogin(false);
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const valid = (value) => /^\d{1,2}$/.test(value) && Number(value) <= 99;
    if (!valid(home) || !valid(away)) { setMessage("Ange båda resultaten, 0–99."); return; }
    if (!account) { setShowLogin(true); setMessage("Logga in med e-post för att spara ditt tips."); return; }
    setBusy(true);
    setMessage("");
    try {
      await request("/predictions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ simulation: simulation.key, predictions: [{ match_id: fixture.id, pred_home: Number(home), pred_away: Number(away), pred_result_type: resultType || null }] }),
      });
      setSaved({ pred_home: Number(home), pred_away: Number(away), pred_result_type: resultType || null });
      setOpen(false);
      setShowLogin(false);
      setMessage("Tipset är sparat.");
      const community = await request(`/prediction-summary?simulation=${encodeURIComponent(simulation.key)}&match_id=${encodeURIComponent(matchId)}`).catch(() => null);
      setSummary(community?.available && community.total >= 2 ? community : null);
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const startLogin = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await request("/auth/start", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, simulation: simulation.key, return_to: `${window.location.pathname}${window.location.search}` }) });
      setWaiting(true);
      window.sessionStorage.setItem("hq-pulse-waiting", "1");
      setMessage("Kolla din e-post. Du kan stanna kvar här medan du bekräftar länken.");
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const canTip = fixture?.can_tip && !simulation.closed && matchStatus?.status_id === 1;
  if (!fixture || (!summary && !canTip && !saved)) return null;
  const shares = summary ? [summary.home, summary.draw, summary.away].map((count) => Math.round(count * 100 / summary.total)) : [];
  const showEditor = canTip && editorVisible;
  const closeEditor = () => { setOpen(false); setShowLogin(false); setMessage(""); };

  return <section className={`match-tip-pulse${!summary && !saved ? " match-tip-pulse--empty" : ""}`} aria-label="Tips för matchen">
    <div className="match-tip-pulse__top">
      <div className="match-tip-pulse__title"><strong>Tippa matchen</strong>{summary && <span>{summary.total} tips</span>}{saved && <span>· Ditt tips {saved.pred_home}–{saved.pred_away}</span>}</div>
      {canTip && <button ref={trigger} type="button" aria-label={saved ? "Ändra tips" : "Tippa"} className="match-tip-pulse__toggle" aria-expanded={showEditor} onClick={startEditing}>{saved ? "Ändra tips" : "Tippa"}</button>}
    </div>
    {summary && <div className="match-tip-pulse__results" aria-label={`Av ${summary.total} tips: ${shares[0]} procent hemma, ${shares[1]} procent oavgjort, ${shares[2]} procent borta`}>
      <div className="match-tip-pulse__bar" aria-hidden="true"><span style={{ width: `${summary.home * 100 / summary.total}%` }} /><span style={{ width: `${summary.draw * 100 / summary.total}%` }} /><span style={{ width: `${summary.away * 100 / summary.total}%` }} /></div>
      <div className="match-tip-pulse__labels"><span>Hemma {shares[0]} %</span><span>Oavgjort {shares[1]} %</span><span>Borta {shares[2]} %</span></div>
    </div>}
    {showEditor && createPortal(<div className="match-tip-pulse__backdrop" onClick={(event) => { if (event.target === event.currentTarget) closeEditor(); }}>
      <div ref={dialog} className="match-tip-pulse match-tip-pulse__dialog" role="dialog" aria-modal="true" aria-labelledby="match-tip-title" onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const controls = Array.from(dialog.current.querySelectorAll("button:not(:disabled),input:not(:disabled),select:not(:disabled)"));
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }}>
      <div className="match-tip-pulse__top"><h2 id="match-tip-title">Tippa matchen</h2><button className="match-tip-pulse__toggle" type="button" onClick={closeEditor}>Stäng</button></div>
      <form className="match-tip-pulse__form" onSubmit={save}>
      <div className="match-tip-pulse__field"><span className="match-tip-pulse__field-label">Resultat</span><div className="match-tip-pulse__score"><input aria-label={`${fixture.home_team} mål`} type="number" min="0" max="99" inputMode="numeric" enterKeyHint="next" value={home} onFocus={(event) => event.target.select()} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); awayInput.current?.focus(); } }} onChange={(event) => { setOpen(true); setHome(event.target.value); }} /><span>–</span><input ref={awayInput} aria-label={`${fixture.away_team} mål`} type="number" min="0" max="99" inputMode="numeric" enterKeyHint="done" value={away} onFocus={(event) => event.target.select()} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); } }} onChange={(event) => { setOpen(true); setAway(event.target.value); }} /></div></div>
      <label className="match-tip-pulse__field"><span className="match-tip-pulse__field-label">Avgörande</span><select aria-label="Avgörande" value={resultType} onChange={(event) => setResultType(event.target.value)}><option value="">Ordinarie tid</option><option value="ef">Förlängning</option><option value="es">Straffar</option></select></label>
      <button type="submit" className="match-tip-pulse__save" disabled={busy}>{busy ? "Sparar…" : "Spara tips"}</button>
    </form>{!account && showLogin && (waiting ? <p className="match-tip-pulse__help">Väntar på att du bekräftar e-postlänken…</p> : <form className="match-tip-pulse__login" onSubmit={startLogin}><label>E-post för att spara tipset <input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label><button type="submit" disabled={busy}>{busy ? "Skickar…" : "Skicka inloggningslänk"}</button></form>) }
      {message && <p className="match-tip-pulse__message" role="status">{message}</p>}
      </div></div>, document.body)}
    {!showEditor && message && <p className={`match-tip-pulse__message${message === "Tipset är sparat." ? " match-tip-pulse__message--saved" : ""}`} role="status">{message}</p>}
  </section>;
}
